import { useState, type FormEvent } from 'react'
import { useSignIn, useSignUp } from '@clerk/clerk-react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowRight, Eye, EyeOff, Github, Lock, Mail, User } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { AuthLayout } from '@/features/auth/components/AuthLayout'
import { AuthNotice, useAuthNotice } from '@/features/auth/components/AuthNotice'
import {
  describeClerkError,
  SIGNIN_ENUMERATION_CODES,
} from '@/features/auth/lib/clerkErrors'
import styles from './LoginPage.module.scss'

type Mode = 'login' | 'signup'

const COPY = {
  login: {
    title: 'Bem-vindo de volta',
    sub: 'Entre para continuar produzindo.',
    cta: 'Entrar',
    social: 'Continuar com GitHub',
    pwAutocomplete: 'current-password',
  },
  signup: {
    title: 'Criar sua conta',
    sub: 'Comece de graça. Sem cartão.',
    cta: 'Criar conta',
    social: 'Cadastrar com GitHub',
    pwAutocomplete: 'new-password',
  },
} as const

const SIGNIN_MASK = 'Email ou senha incorretos. Verifique e tente novamente.'

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPassword2, setShowPassword2] = useState(false)
  const { notice, showError, showInfo, clear } = useAuthNotice()
  const [submitting, setSubmitting] = useState(false)

  const { signIn, isLoaded: signInLoaded, setActive: setActiveSignIn } = useSignIn()
  const { signUp, isLoaded: signUpLoaded, setActive: setActiveSignUp } = useSignUp()
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/login' })

  const copy = COPY[mode]
  const isSignup = mode === 'signup'
  const ready = isSignup ? signUpLoaded : signInLoaded

  function switchMode(next: Mode) {
    setMode(next)
    clear()
    setPassword('')
    setPassword2('')
  }

  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!ready || submitting) return
    clear()
    setSubmitting(true)
    try {
      if (isSignup) {
        if (password !== password2) {
          showError('As senhas não conferem.')
          return
        }
        if (!signUp) return
        const result = await signUp.create({
          emailAddress: email,
          password,
          ...(name ? { firstName: name } : {}),
        })
        if (result.status === 'complete') {
          await setActiveSignUp({ session: result.createdSessionId })
          await navigate({ to: redirect ?? '/' })
        } else {
          // Most common: email verification pending. Surface the next step
          // as info, not error — the signup itself succeeded.
          showInfo(
            'Conta criada',
            'Enviamos um email de confirmação. Abra-o pra ativar a conta antes de entrar.',
          )
        }
      } else {
        if (!signIn) return
        const result = await signIn.create({ identifier: email, password })
        if (result.status === 'complete') {
          await setActiveSignIn({ session: result.createdSessionId })
          await navigate({ to: redirect ?? '/' })
        } else {
          showError(`Não foi possível concluir o login (status: ${String(result.status)}).`)
        }
      }
    } catch (err) {
      // Mask enumeration-leaking codes only on login. Signup intentionally
      // surfaces "email already exists" — UX of generic-message there is awful
      // and the tradeoff vs enumeration was accepted in the security review.
      showError(
        describeClerkError(err, 'Falha na autenticação. Tente novamente.', {
          ...(isSignup
            ? {}
            : { maskCodes: SIGNIN_ENUMERATION_CODES, maskMessage: SIGNIN_MASK }),
        }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGithubOAuth() {
    if (!ready || submitting) return
    clear()
    setSubmitting(true)
    try {
      const flow = isSignup ? signUp : signIn
      if (!flow) return
      await flow.authenticateWithRedirect({
        strategy: 'oauth_github',
        redirectUrl: '/sso-callback',
        redirectUrlComplete: redirect ?? '/',
      })
      // Browser navigates away — control will not return here.
    } catch (err) {
      showError(describeClerkError(err, 'Não foi possível iniciar o login com GitHub.'))
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <div className={styles.tabs} role="tablist" aria-label="Modo de autenticação">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={mode === 'login' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => {
                  switchMode('login')
                }}
              >
                Entrar
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                className={mode === 'signup' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => {
                  switchMode('signup')
                }}
              >
                Criar conta
              </button>
            </div>

            <h1 className={styles.formTitle}>{copy.title}</h1>
            <p className={styles.formSub}>{copy.sub}</p>

            <div className={styles.social}>
              <Button
                type="button"
                variant="secondary"
                block
                // Github do lucide está deprecado e sai na v1.0
                // (lucide-icons/lucide#670). Trocar por outro ícone muda o
                // botão de OAuth que o usuário vê — fica como dívida, não como
                // efeito colateral deste card.
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                leadingIcon={<Github />}
                className={styles.socialBtn}
                onClick={() => {
                  void handleGithubOAuth()
                }}
                disabled={!ready || submitting}
              >
                {copy.social}
              </Button>
            </div>

            <div className={styles.divider}>ou com email</div>

            <form onSubmit={handleEmailSubmit} noValidate>
              {notice ? <AuthNotice notice={notice} onClose={clear} /> : null}

              {isSignup ? (
                <div className={styles.field}>
                  <label htmlFor="auth-name" className={styles.label}>
                    Nome
                  </label>
                  <Input
                    id="auth-name"
                    type="text"
                    placeholder="Como você quer ser chamado"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => { setName(e.target.value); }}
                    leadingIcon={<User />}
                    className={styles.input}
                  />
                </div>
              ) : null}

              <div className={styles.field}>
                <label htmlFor="auth-email" className={styles.label}>
                  Email
                </label>
                <Input
                  id="auth-email"
                  type="email"
                  placeholder="voce@exemplo.com"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); }}
                  leadingIcon={<Mail />}
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="auth-password" className={styles.label}>
                  Senha
                </label>
                <Input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete={copy.pwAutocomplete}
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); }}
                  leadingIcon={<Lock />}
                  trailingIcon={
                    <button
                      type="button"
                      className={styles.eyeBtn}
                      onClick={() => { setShowPassword((v) => !v); }}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  }
                  className={styles.input}
                />
              </div>

              {isSignup ? (
                <div className={styles.field}>
                  <label htmlFor="auth-password2" className={styles.label}>
                    Confirmar senha
                  </label>
                  <Input
                    id="auth-password2"
                    type={showPassword2 ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    value={password2}
                    onChange={(e) => { setPassword2(e.target.value); }}
                    leadingIcon={<Lock />}
                    trailingIcon={
                      <button
                        type="button"
                        className={styles.eyeBtn}
                        onClick={() => { setShowPassword2((v) => !v); }}
                        aria-label={showPassword2 ? 'Ocultar senha' : 'Mostrar senha'}
                        aria-pressed={showPassword2}
                      >
                        {showPassword2 ? <EyeOff /> : <Eye />}
                      </button>
                    }
                    className={styles.input}
                  />
                </div>
              ) : null}

              {!isSignup ? (
                <div className={styles.hintRow}>
                  <label className={styles.check}>
                    <input type="checkbox" defaultChecked />
                    Manter conectado
                  </label>
                  <Link to="/reset-password" className={styles.link}>
                    Esqueci a senha
                  </Link>
                </div>
              ) : (
                <div className={styles.spacer} aria-hidden />
              )}

              <Button
                type="submit"
                variant="primary"
                block
                trailingIcon={<ArrowRight />}
                className={styles.submitBtn}
                disabled={!ready || submitting}
              >
                {copy.cta}
              </Button>
            </form>
    </AuthLayout>
  )
}
